import flatten from "lodash/flatten";
import times from "lodash/times";
import { concurrentActions } from "./serviceHelper";

interface PaginatedFetchOptions<T, Q> {
  /**
   * The query parameters to use for the request
   */
  requestQuery?: Q;
  /**
   * Function to get the total number of items (list size)
   */
  getSummary: (query?: Q) => Promise<{ listSize: number }>;
  /**
   * Function to fetch a page of data
   */
  fetchPage: (
    query: Q & { page: { size: number; number: number } }
  ) => Promise<T[]>;
  /**
   * Maximum number of items to fetch per page
   * @default 100
   */
  pageSize?: number;
  /**
   * Maximum number of concurrent requests
   * @default 5
   */
  concurrentLimit?: number;
  /**
   * Additional parameters to pass to the query
   */
  additionalParams?: any;
}

/**
 * Utility function to fetch all pages of data concurrently with pagination
 *
 * @example
 * ```typescript
 * const payers = await fetchAllPagesWithConcurrency({
 *   requestQuery,
 *   getSummary: () => getPayerRowsSummary(requestQuery),
 *   fetchPage: (query) => getPayerRows(query),
 *   pageSize: 100,
 *   concurrentLimit: 5
 * });
 * ```
 */
export const fetchAllPagesWithConcurrency = async <T, Q = any>({
  requestQuery,
  getSummary,
  fetchPage,
  pageSize = 100,
  concurrentLimit = 5,
  additionalParams
}: PaginatedFetchOptions<T, Q>): Promise<T[]> => {
  // Get the total number of items
  const { listSize } = await getSummary(requestQuery);

  // Calculate the number of pages needed
  const pages = Math.ceil(listSize / pageSize);

  // Create an array of actions to fetch each page
  const actions = times(pages).map(
    (_, i) => () =>
      fetchPage({
        ...requestQuery,
        page: { size: pageSize, number: i + 1 },
        ...additionalParams
      } as any)
  );

  // Execute the actions concurrently with the specified limit
  const allPages = await concurrentActions(actions, {
    concurrentLimit
  });

  // Flatten the results from all pages into a single array
  return flatten(allPages);
};
